import * as angular from 'angular';
import * as _ from 'lodash';

import { Alarm, ComponentDefinition, Dashboard, Group, GroupNameDto, Identifier, IntegrityIssue, IValueViewModel, TagDefinition, ValidationPageConfiguration } from '../../../models/index';
import { IModelIntegrityService } from '../../../services/data/modelIntegrity.service';
import { IStoreService } from '../../../services/index';
import { Store, StoreElement } from '../../../stateManagement';

const categories = [
    { message: 'Groups in loops', icon: 'fa-retweet' },
    { message: 'Missing references', icon: 'fa-chain-broken' },
    { message: 'Out-of-date occurence matrices', icon: 'fa-share-alt fa-rotate-270' },
    { message: 'Duplicated dashboard guids', icon: 'fa-files-o' },
    { message: 'Invalid frequencies', icon: 'fa-clock-o' },
    { message: 'Missing reference groups', icon: 'fa-check' },
    { message: 'Duplicate tag names', icon: 'fa-tags' },
    { message: 'Orphan groups', icon: 'fa-sitemap' },
];

interface IntegrityIssueViewModel {
    alarm?: Alarm;
    dashboard?: Dashboard;
    group?: GroupNameDto;
    groups: Group[];
    identifier?: Identifier;
    identifierViewModel?: IValueViewModel;
    model: IntegrityIssue;
    name: string;
    tag?: TagDefinition;
    tagViewModel?: IValueViewModel;
    validationPageConfiguration?: ValidationPageConfiguration;
}

interface IssueCategoryViewModel {
    count: number;
    hasError: boolean;
    icon: string;
    message: string;
    type: number;
}

class ModelIntegrityStateController implements angular.IComponentController {
    categories: IssueCategoryViewModel[];
    isCollapsed: { [index: number]: boolean } = {};
    isLoading: boolean;
    issues: IntegrityIssueViewModel[] = [];
    loadingMessage: string;
    selectedCategory: IssueCategoryViewModel;
    selectedGroup: Group;
    selectedIssues: IntegrityIssueViewModel[] = [];

    private _store: Store;

    static $inject = ['storeService', 'modelIntegrityService'];

    constructor(
        private _storeService: IStoreService,
        private _modelIntegrityService: IModelIntegrityService
    ) {
    }

    $onInit() {
        this.isLoading = true;
        this._storeService.register(
            store => this.refreshStore(store),
            [
                StoreElement.Identifiers,
                StoreElement.Tags,
                StoreElement.Groups,
                StoreElement.ValueViewModels,
                StoreElement.Dashboards,
                StoreElement.Alarms,
                StoreElement.ValidationPageConfigurations
            ]
        );
    }

    selectIssueType(category: IssueCategoryViewModel) {
        this.selectedCategory = category;
        this.selectedIssues = this.issues.filter(i => i.model.type === category.type);
        this.selectedGroup = undefined;
    }

    private refreshStore(store: Store) {
        this._store = store;
        this.refresh();
    }

    private refresh() {
        this.isLoading = true;
        this._modelIntegrityService
            .getAll()
            .then(data => {
                const issues: IntegrityIssueViewModel[] = data.map(d => this.mapViewModel(d));
                this.issues = issues;
                this.categories = categories.map((c, type) => {
                    const count = issues.filter(i => i.model.type === type).length;
                    return {
                        count,
                        type,
                        icon: c.icon,
                        message: c.message,
                        hasError: count > 0
                    };
                });
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    private mapViewModel(issue: IntegrityIssue): IntegrityIssueViewModel {
        const { identifiers, tags, groups, valueViewModels, dashboards, alarms, validationPageConfigurations } = this._store;
        const group = (issue.group ? groups.dictionary[issue.group] : undefined) as unknown as GroupNameDto;
        const groupList = (issue.groups ? issue.groups.map(g => groups.dictionary[g]) : []);
        const identifier = issue.identifier ? identifiers.dictionary[issue.identifier] : undefined;
        const tag = issue.tag ? tags.dictionary[issue.tag] : undefined;
        const identifierViewModel = issue.identifier ? valueViewModels.dictionary[issue.identifier] : undefined;
        const tagViewModel = issue.tag ? valueViewModels.dictionary[issue.tag] : undefined;
        const dashboard = issue.dashboard ? dashboards.dictionary[issue.dashboard] : undefined;
        const alarm = issue.alarm ? _.find(alarms.dictionary[identifier.guid], a => a.guid === issue.alarm) : undefined;
        const validationPageConfiguration = issue.validationPageConfiguration
            ? validationPageConfigurations.dictionary[issue.validationPageConfiguration]
            : undefined;
        return {
            alarm,
            dashboard,
            group,
            groups: groupList,
            identifier,
            identifierViewModel,
            model: issue,
            name: issue.propertyName,
            tag,
            tagViewModel,
            validationPageConfiguration
        };
    }
}

export const wtModelIntegrityState: ComponentDefinition = {
    componentId: 'wtModelIntegrityState',
    controller: ModelIntegrityStateController,
    template: require('./modelIntegrityState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};